// SPDX-License-Identifier: MIT
// The save half of the git-as-carrier continuity flow: build the GenesisState from a
// project's current row + versions + sessions and write .genesis/ into the project dir,
// so the existing code push (project_git_push) commits and pushes it with the code.
// Injected I/O keeps it unit-testable; it NEVER throws (a failure → false, build is
// never blocked). Wired into welcome.ts triggerCloudSave just BEFORE runCloudSave.
import { buildGenesisState } from '../platform/genesis-build';
import type { GenesisState } from '../platform/genesis-state';
import type { ProjectRow, ProjectVersionRow } from './api';
import type { Session } from '../platform/session-policy';

export interface SaveGenesisDeps {
  getProject: (projectId: string) => Promise<ProjectRow | null>;
  getVersions: (projectId: string) => Promise<ProjectVersionRow[]>;
  getSessions: (projectDir: string) => Promise<Session[]>;
  write: (dir: string, state: GenesisState) => Promise<void>;
}

export async function saveGenesisForProject(
  projectDir: string,
  projectId: string,
  deps: SaveGenesisDeps,
): Promise<boolean> {
  try {
    const project = await deps.getProject(projectId);
    if (!project) return false;
    const [versions, sessions] = await Promise.all([
      deps.getVersions(projectId),
      deps.getSessions(projectDir),
    ]);
    const state: GenesisState = buildGenesisState(project, versions, sessions);
    await deps.write(projectDir, state);
    return true;
  } catch {
    return false;
  }
}
